<template>
  <v-dialog v-model="isOpen" max-width="500" persistent>
    <v-card>
      <v-card-title class="headline">
        Copiar Configuração de API Banking
        <v-spacer />
        <v-btn icon @click="close" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>

        <v-subheader class="px-0 font-weight-bold">Origem</v-subheader>

        <v-alert type="info" dense outlined class="mb-4">
          <div><strong>Tipo:</strong> {{ sourceTypeLabel }}</div>
          <div><strong>Nome:</strong> {{ sourceConfigName }}</div>
        </v-alert>

        <v-divider class="my-4" />

        <!-- Destino -->
        <v-form ref="form" v-model="isFormValid">
          <v-subheader class="px-0 font-weight-bold">Destino</v-subheader>

          <v-radio-group
            v-model="form.target_company_type"
            row
            :rules="[rules.required]"
            @change="onTargetTypeChange"
          >
            <v-radio label="Grupo" value="G" />
            <v-radio label="Empresa" value="C" />
          </v-radio-group>

          <v-autocomplete
            v-model="form.target_company_type_id"
            :items="targetItems"
            :loading="loadingTargetItems"
            :rules="[rules.required, rules.differentFromSource]"
            item-text="name"
            item-value="id"
            label="Selecione o destino"
            outlined
            dense
            clearable
          />

          <!-- Alerta de validação -->
          <v-alert
            v-if="isSameSourceAndTarget"
            type="warning"
            dense
            class="mt-2"
          >
            Origem e destino não podem ser iguais.
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close" :disabled="loading">
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          @click="submit"
          :loading="loading"
          :disabled="!isFormValid || isSameSourceAndTarget"
        >
          Copiar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CompanyGroupRepository from '@/repositories/CompanyGroupRepository';
import CompanyRepository from '@/repositories/CompanyRepository';
import ApiBankingConfigRepository from '@/services/apibanking.service';

export default {
  name: 'CopyConfigDialog',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    sourceConfig: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      isFormValid: false,
      loading: false,
      loadingTargetItems: false,
      targetItems: [],
      form: {
        target_company_type: 'G',
        target_company_type_id: null,
      },
      rules: {
        required: (v) => !!v || 'Campo obrigatório',
        differentFromSource: () => !this.isSameSourceAndTarget || 'Destino deve ser diferente da origem',
      },
      apiBankingConfigRepository: new ApiBankingConfigRepository(),
      companyGroupRepository: new CompanyGroupRepository(),
      companyRepository: new CompanyRepository(),
    };
  },

  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },

    sourceTypeLabel() {
      if (!this.sourceConfig) return '-';
      return this.sourceConfig.company_type === 'G' ? 'Grupo' : 'Empresa';
    },

    sourceConfigName() {
      return this.sourceConfig ? this.sourceConfig.name : '-';
    },

    isSameSourceAndTarget() {
      if (!this.sourceConfig) return false;

      return (
        this.sourceConfig.company_type === this.form.target_company_type
        && this.sourceConfig.company_type_id === this.form.target_company_type_id
      );
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.resetForm();
          this.loadTargetItems();
        }
      },
    },
  },

  methods: {
    async loadTargetItems() {
      this.loadingTargetItems = true;
      try {
        this.targetItems = await this.getItemsByType(this.form.target_company_type);
      } catch (error) {
        console.log(error);
        this.$notification.error('Erro ao carregar lista de destino.');
      } finally {
        this.loadingTargetItems = false;
      }
    },

    async getItemsByType(type) {
      if (type === 'G') {
        const response = await this.companyGroupRepository.getAll();

        return response.map((group) => ({
          id: group.id,
          name: group.description,
        }));
      }

      const response = await this.companyRepository.getAllCompanies(true);

      return response.map((company) => ({
        id: company.id,
        name: company.name,
      }));
    },

    onTargetTypeChange() {
      this.form.target_company_type_id = null;
      this.loadTargetItems();
    },

    resetForm() {
      this.form = {
        target_company_type: 'G',
        target_company_type_id: null,
      };

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    close() {
      this.isOpen = false;
      this.resetForm();
    },

    async submit() {
      if (!this.$refs.form.validate()) {
        return;
      }

      if (this.isSameSourceAndTarget) {
        this.$notification.warn('Origem e destino não podem ser iguais.');
        return;
      }

      this.loading = true;

      try {
        const payload = {
          source_company_type: this.sourceConfig.company_type,
          source_company_type_id: this.sourceConfig.company_type_id,
          target_company_type: this.form.target_company_type,
          target_company_type_id: this.form.target_company_type_id,
          api_banking_service_id: this.sourceConfig.id,
        };

        const response = await this.apiBankingConfigRepository.copyConfiguration(payload);

        this.$notification.success(response.message || 'Configuração copiada com sucesso!');
        this.$emit('copied');
        this.close();
      } catch (error) {
        const message = error.response?.data?.message || 'Ocorreu um erro ao copiar a configuração.';
        this.$notification.error(message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
